<script setup lang="ts">
import { getTranslation } from '@/composables/data-display-utils';
import type { FileDto, Locales, LocalizableString } from '@/services/types/api.types';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    title: string;
    percentage: number;
    imageSrc?: FileDto | null;
    showImages?: boolean;
    fillColor?: string | null;
  }>(),
  {
    showImages: false,
    fillColor: null,
  },
);

const fillPercent = computed(() => `${props.percentage}%`);
</script>

<template>
  <q-card
    flat
    class="base-percentage-bar"
    :style="{
      '--score-fill': fillPercent,
      '--score-fill-color': fillColor,
    }"
  >
    <div class="base-percentage-bar__fill" aria-hidden="true"></div>
    <q-item
      class="base-percentage-bar__content"
      :label="`Choice Label`"
      :caption="`50% (50 votes)`"
      :label-style="{ 'font-weight': 'bold' }"
    >
      <q-item-section v-if="showImages" avatar class="base-percentage-bar__avatar">
        <q-img v-if="imageSrc" :src="imageSrc.url" :ratio="1" class="base-percentage-bar__img" fit="cover" />
        <div v-else class="base-percentage-bar__img base-percentage-bar__img--placeholder">
          <q-icon name="o_image" size="28px" />
        </div>
      </q-item-section>
      <q-item-section class="base-percentage-bar__text">
        <q-item-label class="base-percentage-bar__title">
          {{ title }}
        </q-item-label>
        <q-item-label v-if="$slots.meta" caption class="base-percentage-bar__subtitle">
          <span class="base-percentage-bar__meta">
            <slot name="meta" />
          </span>
        </q-item-label>
      </q-item-section>
      <q-item-section side>
        <q-item-label class="base-percentage-bar__percentage"> {{ percentage }} % </q-item-label>
      </q-item-section>
    </q-item>
  </q-card>
</template>

<style lang="scss">
.base-percentage-bar {
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  margin: $space-sm 0;
}

.base-percentage-bar::before {
  content: '';
  position: absolute;
  inset: 0;
  background: #e6e6e6;
  z-index: 0;
}

.base-percentage-bar__fill {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 1;
  width: var(--score-fill);
  background: var(--score-fill-color, #eaf4fb);
  transition: width 0.35s ease;
}

.base-percentage-bar__content {
  position: relative;
  z-index: 2;
  padding: 16px;
}

.base-percentage-bar__avatar {
  flex: 0 0 auto;
}

.base-percentage-bar__img {
  flex-shrink: 0;
  width: 84px;
  min-width: 84px;
  max-width: 84px;
  height: 84px;
  min-height: 84px;
  max-height: 84px;
  border-radius: 8px;
  overflow: hidden;
}

.base-percentage-bar__img--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #d6d6d6;
  color: #6b6b6b;
}

.base-percentage-bar__text {
  min-width: 0;
}

.base-percentage-bar__title {
  font-size: $small-text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.base-percentage-bar__subtitle {
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums lining-nums;
  font-feature-settings:
    'tnum' 1,
    'lnum' 1;
}

.base-percentage-bar__meta {
  display: inline-flex;
  align-items: center;
  gap: $space-sm;
}

.base-percentage-bar__percentage {
  font-size: $small-text;
  color: $color-neutral-black-text;
  white-space: nowrap;
}
</style>
